import { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''
const SIGNED_OUT = { loading: false, team: null, openRequests: 0, usesTeamEmail: false }
// Shared by every hook instance: one request in flight per user, reused for a few seconds so Navigation and
// the page below it (or route changes) don't each fetch /api/my-team.
const CACHE_MS = 15000
let cache = { userId: null, at: 0, promise: null, value: null }

function loadMyTeam(userId) {
  if (cache.userId === userId && cache.promise && (!cache.value || Date.now() - cache.at < CACHE_MS)) return cache.promise
  const entry = { userId, at: Date.now(), promise: null, value: null }
  entry.promise = fetch(`${API_BASE}/api/my-team`, { credentials: 'include' })
    .then((r) => r.json())
    // Includes requests waiting on them as an association scheduler contact (My team links to that inbox).
    .then((body) => ({ loading: false, team: body.team, usesTeamEmail: body.uses_team_email === true, openRequests: (body.open_requests || 0) + (body.scheduler_requests || 0) }))
    .catch(() => ({ ...SIGNED_OUT }))
    .then((value) => {
      entry.value = value
      // A failed lookup isn't worth remembering.
      if (!value.team && !value.openRequests && cache === entry) cache = { userId: null, at: 0, promise: null, value: null }
      return value
    })
  cache = entry
  return entry.promise
}

/** The team the signed-in user manages (null when none or signed out). */
export function useMyTeam() {
  const { user, isAuthenticated, loading } = useAuth()
  const userId = user?.id || null
  const cached = isAuthenticated && cache.userId === userId && cache.value ? cache.value : null
  const [state, setState] = useState(cached || { loading: true, team: null, openRequests: 0, usesTeamEmail: false })

  useEffect(() => {
    if (loading) return undefined
    if (!isAuthenticated) {
      cache = { userId: null, at: 0, promise: null, value: null }
      setState(SIGNED_OUT)
      return undefined
    }
    let cancelled = false
    loadMyTeam(userId).then((value) => { if (!cancelled) setState(value) })
    return () => { cancelled = true }
  }, [isAuthenticated, loading, userId])

  return state
}

/** Drop the cached team (after the user claims one) so the next useMyTeam() reads it fresh. */
export function forgetMyTeam() {
  cache = { userId: null, at: 0, promise: null, value: null }
}
